import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    SimpleShowLayout,
    SimpleForm,
    TextField,
    TextInput,
    LongTextInput,
    required,
    maxLength,
    minLength,
    regex
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Show from '../admin/Show';
import Edit from '../admin/Edit';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import {US_STATES as usStates} from "../shared/usStates";
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/communication/business';

export const fuseCompanyIcon = Icon;

const styles = {
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
};

/*
 * View: List
 */

// Set title for List Locations
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Companies</span>;

const ListDescription = () => {
    return (
        <span>
            <p><strong>Companies</strong> hold a Company name and your business address.</p>
            <img src="img-diagram-Company-List.png" alt="Diagram of Locations - Companies" style={viewStyles.diagram}/>
        </span>
    );
};

/*
 * List Companies table
 */
export const fuseLocationCompanyList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.city}
                    tertiaryText={record => record.erp}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Name"/>
                    <TextField source="city" label="City"/>
                    <TextField source="state" label="State"/>
                    <TextField source="zipcode" label="Zip"/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * List Companies table
 */
export const fuseLocationCompanyListViewOnly = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.city}
                    tertiaryText={record => record.erp}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Name"/>
                    <TextField source="city" label="City"/>
                    <TextField source="state" label="State"/>
                    <TextField source="zipcode" label="Zip"/>
                    <ViewButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Company
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Company'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Company details below. To edit this Company, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show Company Table
 */
export const fuseLocationCompanyShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="name" label="Company Name"/>
            <TextField source="address" label="Address"/>
            <TextField source="city" label="City" style={viewStyles.rowField}/>
            <TextField source="state" label="State" style={viewStyles.rowField}/>
            <TextField source="zipcode" label="Zip" style={viewStyles.rowField}/>
            <TextField source="erp" label="ERP"/>
        </SimpleShowLayout>
    </Show>
);


/*
 * View: Create form
 */

// Set title for Create Company
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a Company</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>A <strong>Company</strong> holds a Company name and your business address.</p>
            <p>Enter the required fields in the tabs below.</p>
        </span>
    );
};

/*
 * Create Company form
 */
export const fuseLocationCompanyCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm redirect="list">
            <TextInput source="name" label="Name of Company" validate={[required]}/>

            <LongTextInput source="address" label="Address" validate={[required]}/>

            <TextInput source="city" label="City" validate={[required]} style={viewStyles.rowField}/>

            <VirtualizedSelectInput
                addField={true}
                label="State*"
                choices={usStates}
                source="state"
                optionText="abbreviation"
                optionValue="abbreviation"
                errorMessage="Required"
                required={true}
                style={viewStyles.rowField}
                options={{
                    clearable: false,
                    labelKey: "abbreviation",
                    className: "inline short",
                    placeholder: "State*",
                    required: true
                }}
            />

            <TextInput source="zipcode" label="Zip Code"
                       validate={[required, minLength(5, 'minimum 5 characters'), maxLength(5, '5 characters or fewer')]}
                       style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

            <TextInput source="erp" label="ERP for this Company" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                    Enter the <strong>ERP</strong> for this Company.
                    <TooltipIcon dataFor="ttErp"/>
                    <ReactTooltip id="ttErp" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        The <strong>ERP</strong> is used for labeling a Company for Enterprise Resource Planning
                        Software.
                    </ReactTooltip>
                </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Companies List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Company
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Company'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Company fields in the tabs below. Click <strong>Save</strong> to store this Company's details.</p>
        </span>
    );
};

/*
 * Edit Company form
 */
export const fuseLocationCompanyEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextInput source="name" label="Company Name" validate={[required]}/>

            <LongTextInput source="address" label="Address" validate={[required]}/>

            <TextInput source="city" label="City" validate={[required]} style={viewStyles.rowField}/>

            <VirtualizedSelectInput
                addField={true}
                label="State*"
                choices={usStates}
                source="state"
                optionText="abbreviation"
                optionValue="abbreviation"
                errorMessage="Required"
                required={true}
                style={viewStyles.rowField}
                options={{
                    clearable: false,
                    labelKey: "abbreviation",
                    className: "inline short",
                    placeholder: "State*",
                    required: true
                }}
            />

            <TextInput source="zipcode" label="Zip Code"
                       validate={[required, regex(/^\d{5}$/, 'Must be a valid Zip Code')]}
                       style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

            <TextInput source="erp" label="ERP" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                    The <strong>ERP</strong> for this Company.
                    <TooltipIcon dataFor="ttErp"/>
                    <ReactTooltip id="ttErp" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        The <strong>ERP</strong> is used for labeling a Company for Enterprise Resource Planning
                        Software.
                    </ReactTooltip>
                </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Companies List.
            </span>
            <hr style={{...viewStyles.break, ...styles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Location/company.js